import * as t from 'io-ts'
import { EuroCents } from '@espoon-voltti/ts-libs/lib/euro'
import { DateTime } from '@espoon-voltti/ts-libs/lib/time'

import {
  NuoraOrg,
  Org,
  DraftOrgApplicant,
  OrgApplicant,
  DraftNuoraOrg
} from '@espoon-voltti/nuora-domain/applicants'
import {
  GeneralGrantPaperFormData,
  GrantPeriodType
} from '@espoon-voltti/nuora-domain/grants'

import {
  ProjectGrantDraftApp,
  GeneralGrantDraftApp,
  GrantApp,
  PaidApp,
  PaymentWaitApp,
  PostReportApp,
  PreReportApp,
  ProjectGrantFormData,
  GeneralGrantFormData,
  DraftGeneralGrantFormData,
  RecoveredApp,
  RecoveryApp,
  RejectedApp,
  Report,
  SentApp,
  SentData,
  Signer,
  SigningApp,
  SigningData,
  ReviewApp,
  ReviewData,
  ValidDraftApp,
  ValidGeneralGrantDraftApp,
  DraftProjectGrantFormData,
  ProjectGrantType,
  GeneralGrantType
} from '../../domain'

import {
  AttachmentMapper,
  orgDtoToDomain,
  TransactionMapper,
  DraftNuoraOrgDto
} from '@espoon-voltti/nuora-app-common/services/dtos'

import { ApplicantDto, DraftApplicantDto } from './applicants'
import {
  CreateOrUpdateDraftDto,
  DraftAppDto,
  DraftEventDto,
  ProjectGrantFormDataDto,
  PaidAppDto,
  PaymentWaitAppDto,
  PostReportAppDto,
  PreReportAppDto,
  RecoveredAppDto,
  RecoveryAppDto,
  RejectedAppDto,
  ReportDto,
  ReviewAppDto,
  SentAppDto,
  SendForSigningDto,
  SigningAppDto,
  GeneralGrantFormDataDto,
  GeneralGrantPaperFormDataDto
} from './grants'
import { Event } from '@espoon-voltti/nuora-domain/grants'

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace GrantAppMapper {
  export const fromRaw = async (
    app: any,
    signers: Signer[]
  ): Promise<GrantApp> => {
    const validApplicationDto = t
      .union([
        SigningAppDto,
        SentAppDto,
        ReviewAppDto,
        PaymentWaitAppDto,
        PreReportAppDto,
        RejectedAppDto,
        PaidAppDto,
        PostReportAppDto,
        RecoveryAppDto,
        RecoveredAppDto,
        DraftAppDto
      ])
      .decodeOrThrow(app)
    switch (validApplicationDto.state) {
      case 'SIGNING':
        return SigningAppMapper.fromDto(validApplicationDto, signers)

      case 'SENT':
        return SentAppMapper.fromDto(validApplicationDto, signers)

      case 'REVIEW':
        return ReviewAppMapper.fromDto(validApplicationDto, signers)

      case 'PAYMENT_WAIT':
        return PaymentWaitAppMapper.fromDto(validApplicationDto, signers)

      case 'PRE_REPORT':
        return PreReportAppMapper.fromDto(validApplicationDto, signers)

      case 'REJECTED':
        return RejectedAppMapper.fromDto(validApplicationDto, signers)

      case 'PAID':
        return PaidAppMapper.fromDto(validApplicationDto, signers)

      case 'POST_REPORT':
        return PostReportAppMapper.fromDto(validApplicationDto, signers)

      case 'RECOVERY':
        return RecoveryAppMapper.fromDto(validApplicationDto, signers)

      case 'RECOVERED':
        return RecoveredAppMapper.fromDto(validApplicationDto, signers)

      default:
        return DraftAppMapper.fromDto(validApplicationDto)
    }
  }

  export const signResponseFromRaw = async (
    app: any,
    signers: Signer[]
  ): Promise<SigningApp | SentApp> => {
    const validApplicationDto = t
      .union([SigningAppDto, SentAppDto])
      .decodeOrThrow(app)
    if (validApplicationDto.state === 'SIGNING') {
      return SigningAppMapper.fromDto(validApplicationDto, signers)
    }

    return SentAppMapper.fromDto(validApplicationDto, signers)
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DraftAppMapper {
  export const toInitialRequestDto = (
    grantPeriodId: string,
    orgId: string,
    grantPeriodType: GrantPeriodType
  ): CreateOrUpdateDraftDto => ({
    grantPeriodId,
    ...ApplicantMapper.toEmptyDraftDto(orgId),
    form: FormDataMapper.toEmptyDto(grantPeriodType),
    // meta
    state: 'DRAFT'
  })

  export const toDto = (app: ProjectGrantDraftApp): CreateOrUpdateDraftDto => ({
    grantPeriodId: app.grantPeriodId,
    ...ApplicantMapper.toDraftDto(app.applicant),
    form: FormDataMapper.toDto(app.form),
    // meta
    state: 'DRAFT'
  })

  export const toSendForSigningDto = (
    org: Org,
    app: ValidDraftApp | ValidGeneralGrantDraftApp
  ): SendForSigningDto => ({
    grantPeriodId: app.grantPeriodId,
    ...ApplicantMapper.toDto(org, app.applicant.nuoraOrg),
    form: FormDataMapper.toDto(app.form),
    // meta
    state: 'DRAFT'
  })

  export const fromDto = (
    dto: DraftAppDto
  ): ProjectGrantDraftApp | GeneralGrantDraftApp => {
    switch (dto.form.type) {
      case 'PROJECT_GRANT':
      case 'PROJECT_GRANT_PAPER':
        return {
          id: dto.id,
          grantPeriodId: dto.grantPeriodId,
          applicant: {
            type: 'ORGANISATION',
            org: orgDtoToDomain(dto.applicant.org),
            nuoraOrg: draftNuoraOrgFromDto(dto.applicant.nuoraOrg)
          },
          form: DraftFormMapper.projectGrantfromDto(dto.form),
          state: dto.state,
          modifiedAt: dto.modifiedAt
        }
      case 'GENERAL_GRANT':
        return {
          id: dto.id,
          grantPeriodId: dto.grantPeriodId,
          applicant: {
            type: 'ORGANISATION',
            org: orgDtoToDomain(dto.applicant.org),
            nuoraOrg: draftNuoraOrgFromDto(dto.applicant.nuoraOrg)
          },
          form: DraftFormMapper.generalGrantfromDto(dto.form),
          state: dto.state,
          modifiedAt: dto.modifiedAt
        }
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DraftFormMapper {
  export const projectGrantfromDto = (
    dto: ProjectGrantFormDataDto
  ): DraftProjectGrantFormData => {
    return {
      type: 'PROJECT_GRANT',
      event: EventDataMapper.fromDto(dto.event),
      appliedSum: dto.appliedSumInCents
        ? new EuroCents(dto.appliedSumInCents)
        : EuroCents.zero(),
      purpose: dto.purpose ?? '',
      attachments: dto.attachments.map(AttachmentMapper.modelFromDto),
      participants: {
        ages7to20: dto.participants.ages7to20 ?? 0,
        ages21to28: dto.participants.ages21to28 ?? 0,
        ages29andOver: dto.participants.ages29andOver ?? 0
      },
      incomes: TransactionMapper.draftsFromDtos(dto.incomes),
      expenses: TransactionMapper.draftsFromDtos(dto.expenses)
    }
  }

  export const generalGrantfromDto = (
    dto: GeneralGrantFormDataDto
  ): DraftGeneralGrantFormData => {
    return {
      type: 'GENERAL_GRANT',
      appliedSum: dto.appliedSumInCents
        ? new EuroCents(dto.appliedSumInCents)
        : EuroCents.zero(),
      detailsOfUse: dto.detailsOfUse ?? '',
      participants: {
        from7to20: dto.participants.ages7to20,
        from21to28: dto.participants.ages21to28
      },
      goals: {
        goals1: dto.goals.goals1 ?? '',
        goals2: dto.goals.goals2 ?? '',
        goals3: dto.goals.goals3 ?? ''
      },
      youngEspoo: dto.youngEspoo ?? '',
      financialPlan: dto.financialPlan ?? '',
      previousYear: {
        totalProfitInCents: dto.previousYear.totalProfitInCents,
        bankAccountSumInCents: dto.previousYear.bankAccountSumInCents,
        selfAcquiredFundsInCents: dto.previousYear.selfAcquiredFundsInCents,
        receivedGrants: dto.previousYear.receivedGrants ?? ''
      },
      attachments: dto.attachments.map(AttachmentMapper.modelFromDto)
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace SigningAppMapper {
  export const fromDto = (
    dto: SigningAppDto,
    signers: Signer[]
  ): SigningApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace SentAppMapper {
  export const fromDto = (dto: SentAppDto, signers: Signer[]): SentApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ReviewAppMapper {
  export const fromDto = (dto: ReviewAppDto, signers: Signer[]): ReviewApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    review: extractReviewData(dto),
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace PaymentWaitAppMapper {
  export const fromDto = (
    dto: PaymentWaitAppDto,
    signers: Signer[]
  ): PaymentWaitApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    review: extractReviewData(dto),
    decidedAt: dto.decidedAt,
    decision: dto.decision,
    preReport:
      dto.preReport == null ? null : ReportMapper.fromDto(dto.preReport),
    preReportDecision: dto.preReportDecision,
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace PreReportAppMapper {
  export const fromDto = (
    dto: PreReportAppDto,
    signers: Signer[]
  ): PreReportApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    review: extractReviewData(dto),
    decidedAt: dto.decidedAt,
    decision: dto.decision,
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace RejectedAppMapper {
  export const fromDto = (
    dto: RejectedAppDto,
    signers: Signer[]
  ): RejectedApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    review: extractReviewData(dto),
    decidedAt: dto.decidedAt,
    decision: dto.decision,
    preReport:
      dto.preReport == null ? null : ReportMapper.fromDto(dto.preReport),
    preReportDecision: dto.preReportDecision,
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace PaidAppMapper {
  export const fromDto = (dto: PaidAppDto, signers: Signer[]): PaidApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    review: extractReviewData(dto),
    decidedAt: dto.decidedAt,
    decision: dto.decision,
    preReport:
      dto.preReport == null ? null : ReportMapper.fromDto(dto.preReport),
    preReportDecision: dto.preReportDecision,
    paidAt: dto.paidAt,
    postReport:
      dto.postReport == null ? null : ReportMapper.fromDto(dto.postReport),
    postReportDecision: dto.postReportDecision,
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace PostReportAppMapper {
  export const fromDto = (
    dto: PostReportAppDto,
    signers: Signer[]
  ): PostReportApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    review: extractReviewData(dto),
    decidedAt: dto.decidedAt,
    decision: dto.decision,
    paidAt: dto.paidAt,
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace RecoveryAppMapper {
  export const fromDto = (
    dto: RecoveryAppDto,
    signers: Signer[]
  ): RecoveryApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    review: extractReviewData(dto),
    decidedAt: dto.decidedAt,
    decision: dto.decision,
    paidAt: dto.paidAt,
    postReport: ReportMapper.fromDto(dto.postReport),
    postReportDecision: dto.postReportDecision,
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace RecoveredAppMapper {
  export const fromDto = (
    dto: RecoveredAppDto,
    signers: Signer[]
  ): RecoveredApp => ({
    id: dto.id,
    grantPeriodId: dto.grantPeriodId,
    applicant: ApplicantMapper.fromDto(dto.applicant),
    form: FormDataMapper.fromDto(dto.form),
    signing: extractSigningData(dto, signers),
    sent: extractSentData(dto),
    review: extractReviewData(dto),
    decidedAt: dto.decidedAt,
    decision: dto.decision,
    paidAt: dto.paidAt,
    postReport: ReportMapper.fromDto(dto.postReport),
    postReportDecision: dto.postReportDecision,
    state: dto.state
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ApplicantMapper {
  export const fromDto = (dto: ApplicantDto): OrgApplicant => ({
    type: 'ORGANISATION',
    org: orgDtoToDomain(dto.org),
    nuoraOrg: dto.nuoraOrg
  })

  export const toEmptyDraftDto = (orgId: string): DraftApplicantDto => ({
    org: { id: orgId },
    nuoraOrg: {
      activity: null,
      targetGroup: null,
      members: { from7to20: null, from21to28: null, from29: null }
    }
  })

  export const toDraftDto = (domain: DraftOrgApplicant): DraftApplicantDto => ({
    org: { id: domain.org.id },
    nuoraOrg: {
      ...domain.nuoraOrg,
      activity: domain.nuoraOrg.activity || null,
      targetGroup: domain.nuoraOrg.targetGroup || null
    }
  })

  export const toDto = (org: Org, nuoraOrg: NuoraOrg): ApplicantDto => ({
    org: { ...org, attachments: org.attachments.map(AttachmentMapper.toDto) },
    nuoraOrg
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace FormDataMapper {
  export const fromDto = (
    dto:
      | ProjectGrantFormDataDto
      | GeneralGrantFormDataDto
      | GeneralGrantPaperFormDataDto
  ):
    | ProjectGrantFormData
    | GeneralGrantFormData
    | GeneralGrantPaperFormData => {
    switch (dto.type) {
      case 'PROJECT_GRANT':
      case 'PROJECT_GRANT_PAPER':
        return {
          type: 'PROJECT_GRANT',
          event: EventDataMapper.fromDto(dto.event),
          appliedSum: dto.appliedSumInCents
            ? new EuroCents(dto.appliedSumInCents)
            : EuroCents.zero(),
          purpose: dto.purpose ?? '',
          attachments: dto.attachments.map(AttachmentMapper.modelFromDto),
          participants: {
            ages7to20: dto.participants.ages7to20 ?? 0,
            ages21to28: dto.participants.ages21to28 ?? 0,
            ages29andOver: dto.participants.ages29andOver ?? 0
          },
          incomes: TransactionMapper.draftsFromDtos(dto.incomes),
          expenses: TransactionMapper.draftsFromDtos(dto.expenses)
        }
      case 'GENERAL_GRANT':
        return {
          type: 'GENERAL_GRANT',
          appliedSum: dto.appliedSumInCents
            ? new EuroCents(dto.appliedSumInCents)
            : EuroCents.zero(),
          detailsOfUse: dto.detailsOfUse ?? '',
          participants: {
            from7to20: dto.participants.ages7to20 ?? 0,
            from21to28: dto.participants.ages21to28 ?? 0
          },
          goals: {
            goals1: dto.goals.goals1 ?? '',
            goals2: dto.goals.goals2 ?? '',
            goals3: dto.goals.goals3 ?? ''
          },
          youngEspoo: dto.youngEspoo ?? '',
          financialPlan: dto.financialPlan ?? '',
          previousYear: {
            totalProfitInCents: dto.previousYear.totalProfitInCents
              ? dto.previousYear.totalProfitInCents
              : EuroCents.zero(),
            bankAccountSumInCents: dto.previousYear.bankAccountSumInCents
              ? dto.previousYear.bankAccountSumInCents
              : EuroCents.zero(),
            selfAcquiredFundsInCents: dto.previousYear.selfAcquiredFundsInCents
              ? dto.previousYear.selfAcquiredFundsInCents
              : EuroCents.zero(),
            receivedGrants: dto.previousYear.receivedGrants ?? ''
          },
          attachments: dto.attachments.map(AttachmentMapper.modelFromDto)
        }
      case 'GENERAL_GRANT_PAPER':
        return {
          type: dto.type,
          appliedSum: dto.appliedSumInCents
            ? new EuroCents(dto.appliedSumInCents)
            : EuroCents.zero(),
          detailsOfUse: dto.detailsOfUse ?? '',
          participants: {
            from7to20: dto.participants.ages7to20 ?? 0,
            from21to28: dto.participants.ages21to28 ?? 0
          },
          previousYear: {
            totalProfitInCents: dto.previousYear.totalProfitInCents
              ? dto.previousYear.totalProfitInCents
              : EuroCents.zero(),
            bankAccountSumInCents: dto.previousYear.bankAccountSumInCents
              ? dto.previousYear.bankAccountSumInCents
              : EuroCents.zero(),
            selfAcquiredFundsInCents: dto.previousYear.selfAcquiredFundsInCents
              ? dto.previousYear.selfAcquiredFundsInCents
              : EuroCents.zero()
          },
          attachments: dto.attachments.map(AttachmentMapper.modelFromDto)
        }
      default:
        return dto
    }
  }

  export const toEmptyDto = (
    grantPeriodType: GrantPeriodType
  ): GeneralGrantFormDataDto | ProjectGrantFormDataDto => {
    switch (grantPeriodType) {
      case 'YOUTH_PROJECT_GRANT':
        return {
          event: { name: '', location: '', startDate: null, endDate: null },
          appliedSumInCents: 0,
          purpose: '',
          participants: { ages7to20: 0, ages21to28: 0, ages29andOver: 0 },
          expenses: [],
          incomes: [],
          attachments: [],
          type: 'PROJECT_GRANT' as ProjectGrantType
        }
      case 'YOUTH_GENERAL_GRANT':
        return {
          appliedSumInCents: 0,
          detailsOfUse: '',
          participants: { ages7to20: 0, ages21to28: 0 },
          goals: {
            goals1: '',
            goals2: '',
            goals3: ''
          },
          youngEspoo: '',
          financialPlan: '',
          previousYear: {
            totalProfitInCents: null,
            bankAccountSumInCents: null,
            selfAcquiredFundsInCents: null,
            receivedGrants: ''
          },
          attachments: [],
          type: 'GENERAL_GRANT' as GeneralGrantType
        }
      default:
        const _: never = grantPeriodType
        return _
    }
  }

  export const toDto = (
    domain: ProjectGrantFormData | GeneralGrantFormData
  ): ProjectGrantFormDataDto | GeneralGrantFormDataDto => {
    switch (domain.type) {
      case 'PROJECT_GRANT_PAPER':
      case 'PROJECT_GRANT':
        return {
          event: domain.event,
          appliedSumInCents: domain.appliedSum.cents,
          purpose: domain.purpose,
          participants: domain.participants,
          expenses: domain.expenses.map(TransactionMapper.toDto),
          incomes: domain.incomes.map(TransactionMapper.toDto),
          attachments: domain.attachments
            .filter(
              (x) =>
                x.kind === 'PublishedAttachment' ||
                x.kind === 'UploadedAttachment'
            )
            .map(AttachmentMapper.modelToDto),
          type: 'PROJECT_GRANT'
        }
      case 'GENERAL_GRANT':
        return {
          appliedSumInCents: domain.appliedSum.cents,
          detailsOfUse: domain.detailsOfUse,
          participants: {
            ages7to20: domain.participants.from7to20,
            ages21to28: domain.participants.from21to28
          },
          goals: domain.goals,
          youngEspoo: domain.youngEspoo,
          financialPlan: domain.financialPlan,
          previousYear: domain.previousYear,
          attachments: domain.attachments
            .filter(
              (x) =>
                x.kind === 'PublishedAttachment' ||
                x.kind === 'UploadedAttachment'
            )
            .map(AttachmentMapper.modelToDto),
          type: 'GENERAL_GRANT'
        }
      default:
        const _: never = domain
        return _
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace EventDataMapper {
  export const fromDto = (dto: DraftEventDto): Event => ({
    name: dto.name ?? '',
    location: dto.location ?? '',
    startDate: dto.startDate,
    endDate: dto.endDate
  })
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ReportMapper {
  export const fromDto = (dto: ReportDto): Report => ({
    attachments: dto.attachments.map(AttachmentMapper.fromDto)
  })
}

export const extractSigningData = (
  data: { sentForSigningAt?: DateTime },
  signers: Signer[]
): SigningData => ({
  sentForSigningAt: data.sentForSigningAt,
  signers
})

export const extractSentData = (data: { sentAt: Date }): SentData => ({
  sentAt: DateTime.fromDate(data.sentAt)
})

export const extractReviewData = (data: ReviewData): ReviewData => ({
  reviewStartedAt: data.reviewStartedAt
})

export const draftNuoraOrgFromDto = (dto: DraftNuoraOrgDto): DraftNuoraOrg => ({
  activity: dto.activity || '',
  targetGroup: dto.targetGroup || '',
  members: {
    from7to20: dto.members.from7to20 || 0,
    from21to28: dto.members.from21to28 || 0,
    from29: dto.members.from29 || 0
  }
})
